<script>
import { mapState, mapActions } from 'vuex';
/* components */
import FullScreenOverlay from '@/components/FullScreenOverlay';
import GameStep from '@/components/GameStep';
import Button from '@/components/Button';
import ModalOverlay from '@/components/modal/ModalOverlay';
import { requestConnectionCreation } from '@/scripts/api/connections';
import { goTo } from '@/scripts/methods';

/* tools */
import { onboardingBackground } from '@/scripts/computed';
import { mobile } from '@/scripts/computed';

export default {
  name: 'FollowInstructions',
  components: {
    FullScreenOverlay,
    GameStep,
    ModalOverlay,
    Button,
  },
  props: {
    setup: {
      type: Object,
      required: true,
    },
    activeGame: {
      type: String,
      required: true,
    },
    platform: {},
    userInput: {
      type: String,
      required: true,
    },
    linkTo: {
      type: String,
      default: '/onboarding/connect-game',
    },
  },
  data() {
    return {
      slide: null,
    };
  },
  computed: {
    ...mapState(['user', 'showModalSlider']),
    onboardingBackground,
    steps() {
      const { setup } = this;

      if (!setup) return null;

      const { instructionsForm } = setup;
      const result = instructionsForm.steps;

      return result;
    },
    mobile,
  },
  created() {
    const { activeGame, setup } = this;

    if (!activeGame || !setup)
      this.$router.push({
        name: 'Onboarding',
        params: { option: 'connect-game', errorState: true },
      });
  },
  mounted() {},
  watch: {},
  methods: {
    ...mapActions({ toggleModalSlider: 'toggleModalSlider' }),
    changeSlide(increment) {
      const { steps, slide } = this;
      const preResult = slide + increment;
      const result =
        preResult >= 0 ? preResult % steps.length : steps.length - 1;

      this.slide = result;
    },
    showSlider(n) {
      if (!this.mobile) {
        this.slide = n;
        this.toggleModalSlider(true);
      }
    },
    closeSlider() {
      this.slide = null;
      this.toggleModalSlider();
    },
    done() {
      const { activeGame, platform, userInput } = this;
      const params = { gameID: activeGame, userInput };

      if (platform) params.platformID = platform;

      requestConnectionCreation(params)
        .then(() => {
          this.goTo(this.linkTo);
        })
        .catch(() => {
          this.goTo('/onboarding/connect-game');
        });
    },
    goTo,
  },
};
</script>

<template>
  <div>
    <FullScreenOverlay
      :style="onboardingBackground"
      :class="{ shadowBg: onboardingBackground }"
    >
      <div class="onboarding">
        <div class="onboarding__top">
          <Button :back="true" class="back">
            <template #icon>
              <img :src="require(`@/assets/svg/arrow-left.svg`)" />
            </template>
          </Button>
        </div>
        <div class="onboarding__body">
          <div class="onboarding__row onboarding__row--head">
            <h1 class="onboarding__title">
              {{ setup.instructionsForm.title }}
            </h1>
            <div class="onboarding__subtitle">
              {{ setup.instructionsForm.subtitle }}
            </div>
          </div>
          <div class="onboarding__row onboarding__row--list">
            <div :class="{ list: true, left: steps.length > 4 }">
              <!-- map -->
              <div
                class="list__item"
                v-for="(step, index) in steps"
                :key="step.n"
                @click="showSlider(index)"
              >
                <GameStep :type="activeGame" :step="step" />
              </div>
              <!-- // -->
            </div>
          </div>
          <div class="onboarding__row onboarding__row--btn-lg">
            <div
              v-html="setup.instructionsForm.buttonInstructions"
              class="onboarding__description"
            ></div>
            <Button @click="done" :autofocus="true" class="blue default m-290">
              {{ setup.instructionsForm.buttonCaption || 'Ok' }}
            </Button>
          </div>
        </div>
      </div>
    </FullScreenOverlay>
    <ModalOverlay v-if="showModalSlider">
      <div class="modal__dialog">
        <div class="modal__wrapper">
          <div class="modal__content">
            <div class="modal__top">
              <div class="modal__close" @click="closeSlider">
                <img :src="require('@/assets/svg/close-white.svg')" />
              </div>
            </div>
            <div class="modal__middle">
              <div class="modal__back">
                <Button class="back" @click="changeSlide(-1)">
                  <template #icon>
                    <img :src="require(`@/assets/svg/arrow-left.svg`)" />
                  </template>
                </Button>
              </div>

              <!--  -->
              <GameStep
                :mode="'slider'"
                :type="activeGame"
                :step="steps[slide]"
              />

              <div class="modal__next">
                <Button class="back" @click="changeSlide(1)">
                  <template #icon>
                    <img
                      :src="require(`@/assets/svg/arrow-left.svg`)"
                      class="icon-next"
                    />
                  </template>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalOverlay>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables.scss';
.onboarding {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;

  &__title {
    font-family: Sen-Bold;
    font-size: 36px;
    line-height: 43px;
    text-transform: uppercase;
    color: #ffc800;
    text-align: center;
    margin: 0;

    & + .onboarding__subtitle {
      margin-top: 20px;
    }
  }

  &__subtitle {
    font-family: Sen-Bold;
    font-size: 24px;
    line-height: 29px;
    text-transform: capitalize;
    text-align: center;
    background: linear-gradient(
      261deg,
      rgba(255, 91, 148, 1) 45%,
      rgba(132, 65, 164, 1) 57%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &__top {
    margin-bottom: 12px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }

  &__body {
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-grow: 1;
  }

  &__description {
    font-family: Poppins-Regular;
    font-size: 12px;
    line-height: 17px;
    color: rgba(255, 255, 255, 0.7);
    text-align: center;

    & + .button {
      margin-top: 20px;
    }
  }

  &__row {
    & + &--btn-lg {
      margin-top: 50px;
    }

    &--head {
      flex-shrink: 0;
    }

    &--btn-lg {
      display: flex;
      align-items: center;
      flex-direction: column;
      justify-content: flex-end;
      flex-grow: 1;
      width: 100%;
      margin-bottom: 65px;
    }

    &--list {
      margin-top: 76px;
      flex-shrink: 0;
    }
  }
}

.list {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-left: -30px;
  margin-top: -20px;

  &__item {
    width: 270px;
    margin-left: 30px;
    margin-top: 20px;
  }

  &.left {
    justify-content: flex-start;
  }
}

.icon-next {
  transform: scale(-1, 1);
}

.modal {
  &__dialog {
    padding: 50px 20px 40px;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    overflow-y: auto;
  }

  &__top {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 45px;
    padding-right: 28px;
  }

  &__content {
    max-width: 1210px;
    padding: 50px 20px 0;
    max-height: 100%;
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &__wrapper {
    display: flex;
    flex-grow: 1;
    justify-content: center;
    max-height: 100vh;
    width: 100%;
  }

  &__middle {
    padding-bottom: 50px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__close {
    width: 14px;
    height: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    cursor: pointer;

    img {
      display: flex;
      width: 100%;
      height: 100%;
    }

    &:hover {
      opacity: 0.5;
    }
  }

  &__back {
    position: absolute;
    top: 250px;
    left: 0;
    z-index: 1;
  }

  &__next {
    position: absolute;
    top: 250px;
    right: 0;
    z-index: 1;
  }
}

@media (max-width: $mobile) {
  .onboarding {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;

    &__top {
      min-height: 29px;
      margin-bottom: 30px;
      flex-shrink: 0;
    }

    &__title {
      font-size: 24px;
      line-height: 1.28;
    }

    .onboarding__title + .onboarding__subtitle {
      margin-top: 30px;
    }

    &__subtitle {
      font-size: 21px;
      line-height: 26px;
    }

    &__row {
      &--list {
        margin-top: 55px;
      }

      &--btn-lg {
        margin-bottom: 0;
      }
    }
  }

  .list {
    margin-left: 0;
    margin-top: -65px;
    padding-right: 5px;

    &__item {
      margin-top: 0;
      margin-left: 0;
      width: 100%;
      margin-top: 65px;

      & + & {
        margin-top: 65px;
      }
    }
  }
}
</style>
